import { z } from 'zod';
export { z } from 'zod';
import { SPECIALIZATIONS } from '@klaro7/shared-types';

// src/index.ts
var emailSchema = z.string().min(1, "Email is required").email("Invalid email address").toLowerCase().trim();
var passwordSchema = z.string().min(8, "Password must be at least 8 characters").max(100, "Password must be less than 100 characters").regex(/[a-z]/, "Password must contain at least one lowercase letter").regex(/[A-Z]/, "Password must contain at least one uppercase letter").regex(/[0-9]/, "Password must contain at least one number");
var simplePasswordSchema = z.string().min(1, "Password is required").max(100, "Password must be less than 100 characters");
var phoneSchema = z.string().min(10, "Phone number must be at least 10 digits").max(20, "Phone number must be less than 20 characters").regex(/^[\d\s\-+()]+$/, "Invalid phone number format").optional();
var uuidSchema = z.string().uuid("Invalid ID format");
var objectIdSchema = z.string().regex(/^[a-fA-F0-9]{24}$/, "Invalid ID format");
var paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(10),
  sortBy: z.string().optional(),
  sortOrder: z.enum(["asc", "desc"]).default("desc")
});
var searchSchema = paginationSchema.extend({
  q: z.string().min(1).max(100).optional()
});
var dateRangeSchema = z.object({
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
}).refine(
  (data) => {
    if (data.startDate && data.endDate) {
      return data.startDate <= data.endDate;
    }
    return true;
  },
  { message: "Start date must be before end date" }
);
var nameSchema = z.string().min(2, "Name must be at least 2 characters").max(50, "Name must be less than 50 characters").regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, "Name contains invalid characters").trim();
var urlSchema = z.string().url("Invalid URL format").optional();

// src/disposable-email-domains.ts
var BLOCKED_EMAIL_DOMAINS = /* @__PURE__ */ new Set([
  // Attacco 2026-08-26 (rilevato via modulo fraud)
  "dropoffs.org",
  "gomax2025.com",
  "lhadk11.pro",
  "mbmz31.pro",
  "okadk.pro",
  "zynario.pro",
  "ballerstreat.com",
  "beautystoremax.com",
  "faircapride.com",
  "vllalo.pro",
  "asfsadf2.pro",
  // Stesso attore (fingerprint IP/DOB), domini nuovi osservati 2026-08-27 —
  // nessuno dei due era nel dataset pubblico disposable-email-domains
  // (121k+ domini): genera domini freschi, un blocklist statico non basta
  // da solo. Vedi docs/registration-anti-abuse.md § P5 (country allow-list).
  "salack.com",
  "seakol.com",
  "bocably.com",
  "meruado.uk",
  "web-library.net",
  // Provider temp-mail pubblici comuni
  "mailinator.com",
  "guerrillamail.com",
  "guerrillamail.info",
  "10minutemail.com",
  "10minutemail.net",
  "tempmail.com",
  "temp-mail.org",
  "throwawaymail.com",
  "yopmail.com",
  "yopmail.net",
  "trashmail.com",
  "getnada.com",
  "sharklasers.com",
  "maildrop.cc",
  "fakeinbox.com",
  "mailnesia.com",
  "dispostable.com",
  "mintemail.com",
  "mohmal.com",
  "moakt.com"
]);
function isDisposableEmailDomain(email) {
  const at = email.lastIndexOf("@");
  if (at < 0) return false;
  const domain = email.slice(at + 1).trim().toLowerCase();
  return BLOCKED_EMAIL_DOMAINS.has(domain);
}

// src/countries.ts
var COUNTRIES = [
  // --- Italia e destinazioni frequenti ---
  { code: "IT", dial: "+39", flag: "\u{1F1EE}\u{1F1F9}", name: "Italia" },
  { code: "CH", dial: "+41", flag: "\u{1F1E8}\u{1F1ED}", name: "Svizzera" },
  { code: "FR", dial: "+33", flag: "\u{1F1EB}\u{1F1F7}", name: "Francia" },
  { code: "DE", dial: "+49", flag: "\u{1F1E9}\u{1F1EA}", name: "Germania" },
  { code: "ES", dial: "+34", flag: "\u{1F1EA}\u{1F1F8}", name: "Spagna" },
  { code: "GB", dial: "+44", flag: "\u{1F1EC}\u{1F1E7}", name: "Regno Unito" },
  { code: "PT", dial: "+351", flag: "\u{1F1F5}\u{1F1F9}", name: "Portogallo" },
  { code: "BE", dial: "+32", flag: "\u{1F1E7}\u{1F1EA}", name: "Belgio" },
  { code: "NL", dial: "+31", flag: "\u{1F1F3}\u{1F1F1}", name: "Paesi Bassi" },
  { code: "AT", dial: "+43", flag: "\u{1F1E6}\u{1F1F9}", name: "Austria" },
  { code: "IE", dial: "+353", flag: "\u{1F1EE}\u{1F1EA}", name: "Irlanda" },
  { code: "RO", dial: "+40", flag: "\u{1F1F7}\u{1F1F4}", name: "Romania" },
  { code: "PL", dial: "+48", flag: "\u{1F1F5}\u{1F1F1}", name: "Polonia" },
  { code: "AL", dial: "+355", flag: "\u{1F1E6}\u{1F1F1}", name: "Albania" },
  { code: "US", dial: "+1", flag: "\u{1F1FA}\u{1F1F8}", name: "Stati Uniti / Canada" },
  // --- Resto d'Europa ---
  { code: "AD", dial: "+376", flag: "\u{1F1E6}\u{1F1E9}", name: "Andorra" },
  { code: "BA", dial: "+387", flag: "\u{1F1E7}\u{1F1E6}", name: "Bosnia ed Erzegovina" },
  { code: "BG", dial: "+359", flag: "\u{1F1E7}\u{1F1EC}", name: "Bulgaria" },
  { code: "BY", dial: "+375", flag: "\u{1F1E7}\u{1F1FE}", name: "Bielorussia" },
  { code: "CY", dial: "+357", flag: "\u{1F1E8}\u{1F1FE}", name: "Cipro" },
  { code: "CZ", dial: "+420", flag: "\u{1F1E8}\u{1F1FF}", name: "Repubblica Ceca" },
  { code: "DK", dial: "+45", flag: "\u{1F1E9}\u{1F1F0}", name: "Danimarca" },
  { code: "EE", dial: "+372", flag: "\u{1F1EA}\u{1F1EA}", name: "Estonia" },
  { code: "FI", dial: "+358", flag: "\u{1F1EB}\u{1F1EE}", name: "Finlandia" },
  { code: "GR", dial: "+30", flag: "\u{1F1EC}\u{1F1F7}", name: "Grecia" },
  { code: "HR", dial: "+385", flag: "\u{1F1ED}\u{1F1F7}", name: "Croazia" },
  { code: "HU", dial: "+36", flag: "\u{1F1ED}\u{1F1FA}", name: "Ungheria" },
  { code: "IS", dial: "+354", flag: "\u{1F1EE}\u{1F1F8}", name: "Islanda" },
  { code: "LI", dial: "+423", flag: "\u{1F1F1}\u{1F1EE}", name: "Liechtenstein" },
  { code: "LT", dial: "+370", flag: "\u{1F1F1}\u{1F1F9}", name: "Lituania" },
  { code: "LU", dial: "+352", flag: "\u{1F1F1}\u{1F1FA}", name: "Lussemburgo" },
  { code: "LV", dial: "+371", flag: "\u{1F1F1}\u{1F1FB}", name: "Lettonia" },
  { code: "MC", dial: "+377", flag: "\u{1F1F2}\u{1F1E8}", name: "Monaco" },
  { code: "MD", dial: "+373", flag: "\u{1F1F2}\u{1F1E9}", name: "Moldavia" },
  { code: "ME", dial: "+382", flag: "\u{1F1F2}\u{1F1EA}", name: "Montenegro" },
  { code: "MK", dial: "+389", flag: "\u{1F1F2}\u{1F1F0}", name: "Macedonia del Nord" },
  { code: "MT", dial: "+356", flag: "\u{1F1F2}\u{1F1F9}", name: "Malta" },
  { code: "NO", dial: "+47", flag: "\u{1F1F3}\u{1F1F4}", name: "Norvegia" },
  { code: "RS", dial: "+381", flag: "\u{1F1F7}\u{1F1F8}", name: "Serbia" },
  { code: "RU", dial: "+7", flag: "\u{1F1F7}\u{1F1FA}", name: "Russia" },
  { code: "SE", dial: "+46", flag: "\u{1F1F8}\u{1F1EA}", name: "Svezia" },
  { code: "SI", dial: "+386", flag: "\u{1F1F8}\u{1F1EE}", name: "Slovenia" },
  { code: "SK", dial: "+421", flag: "\u{1F1F8}\u{1F1F0}", name: "Slovacchia" },
  { code: "SM", dial: "+378", flag: "\u{1F1F8}\u{1F1F2}", name: "San Marino" },
  { code: "UA", dial: "+380", flag: "\u{1F1FA}\u{1F1E6}", name: "Ucraina" },
  { code: "VA", dial: "+379", flag: "\u{1F1FB}\u{1F1E6}", name: "Citt\xE0 del Vaticano" },
  { code: "XK", dial: "+383", flag: "\u{1F1FD}\u{1F1F0}", name: "Kosovo" },
  // --- Africa ---
  { code: "DZ", dial: "+213", flag: "\u{1F1E9}\u{1F1FF}", name: "Algeria" },
  { code: "AO", dial: "+244", flag: "\u{1F1E6}\u{1F1F4}", name: "Angola" },
  { code: "BJ", dial: "+229", flag: "\u{1F1E7}\u{1F1EF}", name: "Benin" },
  { code: "BW", dial: "+267", flag: "\u{1F1E7}\u{1F1FC}", name: "Botswana" },
  { code: "BF", dial: "+226", flag: "\u{1F1E7}\u{1F1EB}", name: "Burkina Faso" },
  { code: "BI", dial: "+257", flag: "\u{1F1E7}\u{1F1EE}", name: "Burundi" },
  { code: "CM", dial: "+237", flag: "\u{1F1E8}\u{1F1F2}", name: "Camerun" },
  { code: "CV", dial: "+238", flag: "\u{1F1E8}\u{1F1FB}", name: "Capo Verde" },
  { code: "TD", dial: "+235", flag: "\u{1F1F9}\u{1F1E9}", name: "Ciad" },
  { code: "KM", dial: "+269", flag: "\u{1F1F0}\u{1F1F2}", name: "Comore" },
  { code: "CG", dial: "+242", flag: "\u{1F1E8}\u{1F1EC}", name: "Congo" },
  { code: "CD", dial: "+243", flag: "\u{1F1E8}\u{1F1E9}", name: "Congo (RD)" },
  { code: "CI", dial: "+225", flag: "\u{1F1E8}\u{1F1EE}", name: "Costa d'Avorio" },
  { code: "EG", dial: "+20", flag: "\u{1F1EA}\u{1F1EC}", name: "Egitto" },
  { code: "ER", dial: "+291", flag: "\u{1F1EA}\u{1F1F7}", name: "Eritrea" },
  { code: "SZ", dial: "+268", flag: "\u{1F1F8}\u{1F1FF}", name: "Eswatini" },
  { code: "ET", dial: "+251", flag: "\u{1F1EA}\u{1F1F9}", name: "Etiopia" },
  { code: "GA", dial: "+241", flag: "\u{1F1EC}\u{1F1E6}", name: "Gabon" },
  { code: "GM", dial: "+220", flag: "\u{1F1EC}\u{1F1F2}", name: "Gambia" },
  { code: "GH", dial: "+233", flag: "\u{1F1EC}\u{1F1ED}", name: "Ghana" },
  { code: "DJ", dial: "+253", flag: "\u{1F1E9}\u{1F1EF}", name: "Gibuti" },
  { code: "GN", dial: "+224", flag: "\u{1F1EC}\u{1F1F3}", name: "Guinea" },
  { code: "GQ", dial: "+240", flag: "\u{1F1EC}\u{1F1F6}", name: "Guinea Equatoriale" },
  { code: "GW", dial: "+245", flag: "\u{1F1EC}\u{1F1FC}", name: "Guinea-Bissau" },
  { code: "KE", dial: "+254", flag: "\u{1F1F0}\u{1F1EA}", name: "Kenya" },
  { code: "LS", dial: "+266", flag: "\u{1F1F1}\u{1F1F8}", name: "Lesotho" },
  { code: "LR", dial: "+231", flag: "\u{1F1F1}\u{1F1F7}", name: "Liberia" },
  { code: "LY", dial: "+218", flag: "\u{1F1F1}\u{1F1FE}", name: "Libia" },
  { code: "MG", dial: "+261", flag: "\u{1F1F2}\u{1F1EC}", name: "Madagascar" },
  { code: "MW", dial: "+265", flag: "\u{1F1F2}\u{1F1FC}", name: "Malawi" },
  { code: "ML", dial: "+223", flag: "\u{1F1F2}\u{1F1F1}", name: "Mali" },
  { code: "MA", dial: "+212", flag: "\u{1F1F2}\u{1F1E6}", name: "Marocco" },
  { code: "MR", dial: "+222", flag: "\u{1F1F2}\u{1F1F7}", name: "Mauritania" },
  { code: "MU", dial: "+230", flag: "\u{1F1F2}\u{1F1FA}", name: "Mauritius" },
  { code: "MZ", dial: "+258", flag: "\u{1F1F2}\u{1F1FF}", name: "Mozambico" },
  { code: "NA", dial: "+264", flag: "\u{1F1F3}\u{1F1E6}", name: "Namibia" },
  { code: "NE", dial: "+227", flag: "\u{1F1F3}\u{1F1EA}", name: "Niger" },
  { code: "NG", dial: "+234", flag: "\u{1F1F3}\u{1F1EC}", name: "Nigeria" },
  { code: "CF", dial: "+236", flag: "\u{1F1E8}\u{1F1EB}", name: "Repubblica Centrafricana" },
  { code: "RW", dial: "+250", flag: "\u{1F1F7}\u{1F1FC}", name: "Ruanda" },
  { code: "EH", dial: "+212", flag: "\u{1F1EA}\u{1F1ED}", name: "Sahara Occidentale" },
  { code: "ST", dial: "+239", flag: "\u{1F1F8}\u{1F1F9}", name: "S\xE3o Tom\xE9 e Pr\xEDncipe" },
  { code: "SN", dial: "+221", flag: "\u{1F1F8}\u{1F1F3}", name: "Senegal" },
  { code: "SC", dial: "+248", flag: "\u{1F1F8}\u{1F1E8}", name: "Seychelles" },
  { code: "SL", dial: "+232", flag: "\u{1F1F8}\u{1F1F1}", name: "Sierra Leone" },
  { code: "SO", dial: "+252", flag: "\u{1F1F8}\u{1F1F4}", name: "Somalia" },
  { code: "ZA", dial: "+27", flag: "\u{1F1FF}\u{1F1E6}", name: "Sudafrica" },
  { code: "SD", dial: "+249", flag: "\u{1F1F8}\u{1F1E9}", name: "Sudan" },
  { code: "SS", dial: "+211", flag: "\u{1F1F8}\u{1F1F8}", name: "Sudan del Sud" },
  { code: "TZ", dial: "+255", flag: "\u{1F1F9}\u{1F1FF}", name: "Tanzania" },
  { code: "TG", dial: "+228", flag: "\u{1F1F9}\u{1F1EC}", name: "Togo" },
  { code: "TN", dial: "+216", flag: "\u{1F1F9}\u{1F1F3}", name: "Tunisia" },
  { code: "UG", dial: "+256", flag: "\u{1F1FA}\u{1F1EC}", name: "Uganda" },
  { code: "ZM", dial: "+260", flag: "\u{1F1FF}\u{1F1F2}", name: "Zambia" },
  { code: "ZW", dial: "+263", flag: "\u{1F1FF}\u{1F1FC}", name: "Zimbabwe" },
  // --- Americhe ---
  { code: "AI", dial: "+1264", flag: "\u{1F1E6}\u{1F1EE}", name: "Anguilla" },
  { code: "AG", dial: "+1268", flag: "\u{1F1E6}\u{1F1EC}", name: "Antigua e Barbuda" },
  { code: "AR", dial: "+54", flag: "\u{1F1E6}\u{1F1F7}", name: "Argentina" },
  { code: "AW", dial: "+297", flag: "\u{1F1E6}\u{1F1FC}", name: "Aruba" },
  { code: "BS", dial: "+1242", flag: "\u{1F1E7}\u{1F1F8}", name: "Bahamas" },
  { code: "BB", dial: "+1246", flag: "\u{1F1E7}\u{1F1E7}", name: "Barbados" },
  { code: "BZ", dial: "+501", flag: "\u{1F1E7}\u{1F1FF}", name: "Belize" },
  { code: "BM", dial: "+1441", flag: "\u{1F1E7}\u{1F1F2}", name: "Bermuda" },
  { code: "BO", dial: "+591", flag: "\u{1F1E7}\u{1F1F4}", name: "Bolivia" },
  { code: "BR", dial: "+55", flag: "\u{1F1E7}\u{1F1F7}", name: "Brasile" },
  { code: "CL", dial: "+56", flag: "\u{1F1E8}\u{1F1F1}", name: "Cile" },
  { code: "CO", dial: "+57", flag: "\u{1F1E8}\u{1F1F4}", name: "Colombia" },
  { code: "CR", dial: "+506", flag: "\u{1F1E8}\u{1F1F7}", name: "Costa Rica" },
  { code: "CU", dial: "+53", flag: "\u{1F1E8}\u{1F1FA}", name: "Cuba" },
  { code: "CW", dial: "+599", flag: "\u{1F1E8}\u{1F1FC}", name: "Cura\xE7ao" },
  { code: "DM", dial: "+1767", flag: "\u{1F1E9}\u{1F1F2}", name: "Dominica" },
  { code: "EC", dial: "+593", flag: "\u{1F1EA}\u{1F1E8}", name: "Ecuador" },
  { code: "SV", dial: "+503", flag: "\u{1F1F8}\u{1F1FB}", name: "El Salvador" },
  { code: "JM", dial: "+1876", flag: "\u{1F1EF}\u{1F1F2}", name: "Giamaica" },
  { code: "GD", dial: "+1473", flag: "\u{1F1EC}\u{1F1E9}", name: "Grenada" },
  { code: "GP", dial: "+590", flag: "\u{1F1EC}\u{1F1F5}", name: "Guadalupa" },
  { code: "GT", dial: "+502", flag: "\u{1F1EC}\u{1F1F9}", name: "Guatemala" },
  { code: "GY", dial: "+592", flag: "\u{1F1EC}\u{1F1FE}", name: "Guyana" },
  { code: "GF", dial: "+594", flag: "\u{1F1EC}\u{1F1EB}", name: "Guyana Francese" },
  { code: "HT", dial: "+509", flag: "\u{1F1ED}\u{1F1F9}", name: "Haiti" },
  { code: "HN", dial: "+504", flag: "\u{1F1ED}\u{1F1F3}", name: "Honduras" },
  { code: "KY", dial: "+1345", flag: "\u{1F1F0}\u{1F1FE}", name: "Isole Cayman" },
  { code: "FK", dial: "+500", flag: "\u{1F1EB}\u{1F1F0}", name: "Isole Falkland" },
  { code: "TC", dial: "+1649", flag: "\u{1F1F9}\u{1F1E8}", name: "Isole Turks e Caicos" },
  { code: "VG", dial: "+1284", flag: "\u{1F1FB}\u{1F1EC}", name: "Isole Vergini Britanniche" },
  { code: "MQ", dial: "+596", flag: "\u{1F1F2}\u{1F1F6}", name: "Martinica" },
  { code: "MX", dial: "+52", flag: "\u{1F1F2}\u{1F1FD}", name: "Messico" },
  { code: "MS", dial: "+1664", flag: "\u{1F1F2}\u{1F1F8}", name: "Montserrat" },
  { code: "NI", dial: "+505", flag: "\u{1F1F3}\u{1F1EE}", name: "Nicaragua" },
  { code: "PA", dial: "+507", flag: "\u{1F1F5}\u{1F1E6}", name: "Panama" },
  { code: "PY", dial: "+595", flag: "\u{1F1F5}\u{1F1FE}", name: "Paraguay" },
  { code: "PE", dial: "+51", flag: "\u{1F1F5}\u{1F1EA}", name: "Per\xF9" },
  { code: "PR", dial: "+1787", flag: "\u{1F1F5}\u{1F1F7}", name: "Portorico" },
  { code: "DO", dial: "+1809", flag: "\u{1F1E9}\u{1F1F4}", name: "Repubblica Dominicana" },
  { code: "KN", dial: "+1869", flag: "\u{1F1F0}\u{1F1F3}", name: "Saint Kitts e Nevis" },
  { code: "LC", dial: "+1758", flag: "\u{1F1F1}\u{1F1E8}", name: "Saint Lucia" },
  { code: "VC", dial: "+1784", flag: "\u{1F1FB}\u{1F1E8}", name: "Saint Vincent e Grenadine" },
  { code: "SR", dial: "+597", flag: "\u{1F1F8}\u{1F1F7}", name: "Suriname" },
  { code: "TT", dial: "+1868", flag: "\u{1F1F9}\u{1F1F9}", name: "Trinidad e Tobago" },
  { code: "UY", dial: "+598", flag: "\u{1F1FA}\u{1F1FE}", name: "Uruguay" },
  { code: "VE", dial: "+58", flag: "\u{1F1FB}\u{1F1EA}", name: "Venezuela" },
  // --- Asia ---
  { code: "AF", dial: "+93", flag: "\u{1F1E6}\u{1F1EB}", name: "Afghanistan" },
  { code: "SA", dial: "+966", flag: "\u{1F1F8}\u{1F1E6}", name: "Arabia Saudita" },
  { code: "AM", dial: "+374", flag: "\u{1F1E6}\u{1F1F2}", name: "Armenia" },
  { code: "AZ", dial: "+994", flag: "\u{1F1E6}\u{1F1FF}", name: "Azerbaigian" },
  { code: "BH", dial: "+973", flag: "\u{1F1E7}\u{1F1ED}", name: "Bahrein" },
  { code: "BD", dial: "+880", flag: "\u{1F1E7}\u{1F1E9}", name: "Bangladesh" },
  { code: "BT", dial: "+975", flag: "\u{1F1E7}\u{1F1F9}", name: "Bhutan" },
  { code: "BN", dial: "+673", flag: "\u{1F1E7}\u{1F1F3}", name: "Brunei" },
  { code: "KH", dial: "+855", flag: "\u{1F1F0}\u{1F1ED}", name: "Cambogia" },
  { code: "CN", dial: "+86", flag: "\u{1F1E8}\u{1F1F3}", name: "Cina" },
  { code: "KP", dial: "+850", flag: "\u{1F1F0}\u{1F1F5}", name: "Corea del Nord" },
  { code: "KR", dial: "+82", flag: "\u{1F1F0}\u{1F1F7}", name: "Corea del Sud" },
  { code: "AE", dial: "+971", flag: "\u{1F1E6}\u{1F1EA}", name: "Emirati Arabi Uniti" },
  { code: "PH", dial: "+63", flag: "\u{1F1F5}\u{1F1ED}", name: "Filippine" },
  { code: "GE", dial: "+995", flag: "\u{1F1EC}\u{1F1EA}", name: "Georgia" },
  { code: "JP", dial: "+81", flag: "\u{1F1EF}\u{1F1F5}", name: "Giappone" },
  { code: "JO", dial: "+962", flag: "\u{1F1EF}\u{1F1F4}", name: "Giordania" },
  { code: "HK", dial: "+852", flag: "\u{1F1ED}\u{1F1F0}", name: "Hong Kong" },
  { code: "IN", dial: "+91", flag: "\u{1F1EE}\u{1F1F3}", name: "India" },
  { code: "ID", dial: "+62", flag: "\u{1F1EE}\u{1F1E9}", name: "Indonesia" },
  { code: "IR", dial: "+98", flag: "\u{1F1EE}\u{1F1F7}", name: "Iran" },
  { code: "IQ", dial: "+964", flag: "\u{1F1EE}\u{1F1F6}", name: "Iraq" },
  { code: "IL", dial: "+972", flag: "\u{1F1EE}\u{1F1F1}", name: "Israele" },
  { code: "KZ", dial: "+7", flag: "\u{1F1F0}\u{1F1FF}", name: "Kazakistan" },
  { code: "KG", dial: "+996", flag: "\u{1F1F0}\u{1F1EC}", name: "Kirghizistan" },
  { code: "KW", dial: "+965", flag: "\u{1F1F0}\u{1F1FC}", name: "Kuwait" },
  { code: "LA", dial: "+856", flag: "\u{1F1F1}\u{1F1E6}", name: "Laos" },
  { code: "LB", dial: "+961", flag: "\u{1F1F1}\u{1F1E7}", name: "Libano" },
  { code: "MO", dial: "+853", flag: "\u{1F1F2}\u{1F1F4}", name: "Macao" },
  { code: "MV", dial: "+960", flag: "\u{1F1F2}\u{1F1FB}", name: "Maldive" },
  { code: "MY", dial: "+60", flag: "\u{1F1F2}\u{1F1FE}", name: "Malesia" },
  { code: "MN", dial: "+976", flag: "\u{1F1F2}\u{1F1F3}", name: "Mongolia" },
  { code: "MM", dial: "+95", flag: "\u{1F1F2}\u{1F1F2}", name: "Myanmar" },
  { code: "NP", dial: "+977", flag: "\u{1F1F3}\u{1F1F5}", name: "Nepal" },
  { code: "OM", dial: "+968", flag: "\u{1F1F4}\u{1F1F2}", name: "Oman" },
  { code: "PK", dial: "+92", flag: "\u{1F1F5}\u{1F1F0}", name: "Pakistan" },
  { code: "PS", dial: "+970", flag: "\u{1F1F5}\u{1F1F8}", name: "Palestina" },
  { code: "QA", dial: "+974", flag: "\u{1F1F6}\u{1F1E6}", name: "Qatar" },
  { code: "SG", dial: "+65", flag: "\u{1F1F8}\u{1F1EC}", name: "Singapore" },
  { code: "SY", dial: "+963", flag: "\u{1F1F8}\u{1F1FE}", name: "Siria" },
  { code: "LK", dial: "+94", flag: "\u{1F1F1}\u{1F1F0}", name: "Sri Lanka" },
  { code: "TJ", dial: "+992", flag: "\u{1F1F9}\u{1F1EF}", name: "Tagikistan" },
  { code: "TW", dial: "+886", flag: "\u{1F1F9}\u{1F1FC}", name: "Taiwan" },
  { code: "TH", dial: "+66", flag: "\u{1F1F9}\u{1F1ED}", name: "Thailandia" },
  { code: "TL", dial: "+670", flag: "\u{1F1F9}\u{1F1F1}", name: "Timor Est" },
  { code: "TR", dial: "+90", flag: "\u{1F1F9}\u{1F1F7}", name: "Turchia" },
  { code: "TM", dial: "+993", flag: "\u{1F1F9}\u{1F1F2}", name: "Turkmenistan" },
  { code: "UZ", dial: "+998", flag: "\u{1F1FA}\u{1F1FF}", name: "Uzbekistan" },
  { code: "VN", dial: "+84", flag: "\u{1F1FB}\u{1F1F3}", name: "Vietnam" },
  { code: "YE", dial: "+967", flag: "\u{1F1FE}\u{1F1EA}", name: "Yemen" },
  // --- Oceania ---
  { code: "AU", dial: "+61", flag: "\u{1F1E6}\u{1F1FA}", name: "Australia" },
  { code: "FJ", dial: "+679", flag: "\u{1F1EB}\u{1F1EF}", name: "Figi" },
  { code: "GU", dial: "+1671", flag: "\u{1F1EC}\u{1F1FA}", name: "Guam" },
  { code: "NF", dial: "+672", flag: "\u{1F1F3}\u{1F1EB}", name: "Isola Norfolk" },
  { code: "CK", dial: "+682", flag: "\u{1F1E8}\u{1F1F0}", name: "Isole Cook" },
  { code: "MP", dial: "+1670", flag: "\u{1F1F2}\u{1F1F5}", name: "Isole Marianne Settentrionali" },
  { code: "MH", dial: "+692", flag: "\u{1F1F2}\u{1F1ED}", name: "Isole Marshall" },
  { code: "SB", dial: "+677", flag: "\u{1F1F8}\u{1F1E7}", name: "Isole Salomone" },
  { code: "KI", dial: "+686", flag: "\u{1F1F0}\u{1F1EE}", name: "Kiribati" },
  { code: "FM", dial: "+691", flag: "\u{1F1EB}\u{1F1F2}", name: "Micronesia" },
  { code: "NR", dial: "+674", flag: "\u{1F1F3}\u{1F1F7}", name: "Nauru" },
  { code: "NU", dial: "+683", flag: "\u{1F1F3}\u{1F1FA}", name: "Niue" },
  { code: "NC", dial: "+687", flag: "\u{1F1F3}\u{1F1E8}", name: "Nuova Caledonia" },
  { code: "NZ", dial: "+64", flag: "\u{1F1F3}\u{1F1FF}", name: "Nuova Zelanda" },
  { code: "PW", dial: "+680", flag: "\u{1F1F5}\u{1F1FC}", name: "Palau" },
  { code: "PG", dial: "+675", flag: "\u{1F1F5}\u{1F1EC}", name: "Papua Nuova Guinea" },
  { code: "PF", dial: "+689", flag: "\u{1F1F5}\u{1F1EB}", name: "Polinesia Francese" },
  { code: "WS", dial: "+685", flag: "\u{1F1FC}\u{1F1F8}", name: "Samoa" },
  { code: "AS", dial: "+1684", flag: "\u{1F1E6}\u{1F1F8}", name: "Samoa Americane" },
  { code: "TK", dial: "+690", flag: "\u{1F1F9}\u{1F1F0}", name: "Tokelau" },
  { code: "TO", dial: "+676", flag: "\u{1F1F9}\u{1F1F4}", name: "Tonga" },
  { code: "TV", dial: "+688", flag: "\u{1F1F9}\u{1F1FB}", name: "Tuvalu" },
  { code: "VU", dial: "+678", flag: "\u{1F1FB}\u{1F1FA}", name: "Vanuatu" },
  { code: "WF", dial: "+681", flag: "\u{1F1FC}\u{1F1EB}", name: "Wallis e Futuna" }
];

// src/phone.ts
var KEEPS_LEADING_ZERO = /* @__PURE__ */ new Set(["+39"]);
var DIALS_LONGEST_FIRST = [...new Set(COUNTRIES.map((c) => c.dial))].sort(
  (a, b) => b.length - a.length
);
function stripPhoneSeparators(value) {
  return (value || "").replace(/[\s .\-()]/g, "");
}
function composeE164(dial, national) {
  const digits = (national || "").replace(/\D/g, "");
  const trimmed = KEEPS_LEADING_ZERO.has(dial) ? digits : digits.replace(/^0+/, "");
  return trimmed ? `${dial}${trimmed}` : "";
}
function isItalianNonMobile(value) {
  const cleaned = normalizeE164(value);
  return cleaned.startsWith("+39") && !cleaned.startsWith("+393");
}
function normalizeE164(value) {
  const cleaned = stripPhoneSeparators(value);
  if (!cleaned.startsWith("+")) return cleaned;
  const dial = DIALS_LONGEST_FIRST.find((d) => cleaned.startsWith(d));
  if (!dial) return cleaned;
  return composeE164(dial, cleaned.slice(dial.length)) || cleaned;
}
function dialPrefixOf(phone) {
  const cleaned = stripPhoneSeparators(phone);
  return DIALS_LONGEST_FIRST.find((d) => cleaned.startsWith(d)) ?? null;
}

// src/auth.validators.ts
var loginSchema = z.object({
  email: emailSchema,
  password: simplePasswordSchema
});
function isAdult(dob, now = /* @__PURE__ */ new Date()) {
  if (Number.isNaN(dob.getTime())) return false;
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || monthDiff === 0 && now.getDate() < dob.getDate()) {
    age -= 1;
  }
  return age >= 18;
}
var registerSchema = z.object({
  email: emailSchema.refine((v) => !isDisposableEmailDomain(v), {
    message: "Non \xE8 possibile registrarsi con un indirizzo email temporaneo."
  }),
  password: passwordSchema,
  confirmPassword: z.string().min(1, "Please confirm your password"),
  firstName: nameSchema,
  lastName: nameSchema,
  // Phone is REQUIRED at registration (E.164). The client sends it composed by
  // the country selector (e.g. +39XXXXXXXXXX); it gates SMS/voice notifications.
  phone: z.string({ required_error: "Il numero di telefono \xE8 obbligatorio" }).regex(/^\+[1-9]\d{6,14}$/, "Numero di telefono non valido (formato internazionale, es. +39...)").refine((v) => !isItalianNonMobile(v), {
    message: "Serve un numero di cellulare: su un numero fisso non pu\xF2 arrivare l'SMS di verifica."
  }),
  dateOfBirth: z.coerce.date().refine((d) => isAdult(d), {
    message: "Devi avere almeno 18 anni per registrarti"
  }),
  acceptTerms: z.literal(true, {
    errorMap: () => ({ message: "Devi accettare i Termini e Condizioni" })
  }),
  acceptPrivacy: z.literal(true, {
    errorMap: () => ({ message: "Devi accettare la Privacy Policy" })
  }),
  marketing: z.boolean().optional(),
  profiling: z.boolean().optional(),
  // Optional MGM referral code ("Porta un Amico"). An invalid/unknown code is
  // silently ignored server-side and never blocks registration.
  referralCode: z.string().trim().min(1).max(32).optional(),
  // Cloudflare Turnstile anti-bot token. Verified server-side only when
  // TURNSTILE_SECRET_KEY is configured; absent otherwise.
  turnstileToken: z.string().optional()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var completeOnboardingSchema = z.object({
  dateOfBirth: z.coerce.date().refine((d) => isAdult(d), {
    message: "Devi avere almeno 18 anni"
  }).optional(),
  acceptTerms: z.literal(true, {
    errorMap: () => ({ message: "Devi accettare i Termini e Condizioni" })
  }),
  acceptPrivacy: z.literal(true, {
    errorMap: () => ({ message: "Devi accettare la Privacy Policy" })
  }),
  marketing: z.boolean().optional(),
  profiling: z.boolean().optional()
});
var updateMyProfileSchema = z.object({
  profile: z.object({
    firstName: nameSchema.optional(),
    lastName: nameSchema.optional(),
    nickname: z.string().trim().max(50, "Il nickname non pu\xF2 superare i 50 caratteri").optional(),
    phone: z.string().regex(/^\+[1-9]\d{6,14}$/, "Numero di telefono non valido (formato internazionale, es. +39...)").optional(),
    dateOfBirth: z.coerce.date().refine((d) => isAdult(d), {
      message: "Devi avere almeno 18 anni"
    }).optional(),
    bio: z.string().max(500, "La bio non pu\xF2 superare i 500 caratteri").optional()
  })
});
var refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, "Refresh token is required")
});
var passwordResetRequestSchema = z.object({
  email: emailSchema
});
var passwordResetConfirmSchema = z.object({
  token: z.string().min(1, "Reset token is required"),
  newPassword: passwordSchema,
  confirmPassword: z.string().min(1, "Please confirm your password")
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var confirmEmailSchema = z.object({
  token: z.string().min(1, "Il token di conferma \xE8 obbligatorio")
});
var changePasswordSchema = z.object({
  currentPassword: simplePasswordSchema,
  newPassword: passwordSchema,
  confirmPassword: z.string().min(1, "Please confirm your password")
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
}).refine((data) => data.currentPassword !== data.newPassword, {
  message: "New password must be different from current password",
  path: ["newPassword"]
});
var selfChangePasswordSchema = z.object({
  newPassword: passwordSchema,
  confirmPassword: z.string().min(1, "Please confirm your password")
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var userRoleValues = ["super_admin", "admin", "professional", "user"];
var userStatusValues = ["active", "inactive", "pending", "waiting_questionnaire", "suspended"];
var userRoleSchema = z.enum(userRoleValues);
var userStatusSchema = z.enum(userStatusValues);
var createUserSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  role: userRoleSchema.optional().default("user"),
  profile: z.object({
    firstName: nameSchema,
    lastName: nameSchema,
    phone: phoneSchema.optional()
  })
});
var updateUserSchema = z.object({
  email: emailSchema.optional(),
  role: userRoleSchema.optional(),
  status: userStatusSchema.optional(),
  profile: z.object({
    firstName: nameSchema.optional(),
    lastName: nameSchema.optional(),
    phone: phoneSchema,
    avatar: urlSchema,
    bio: z.string().max(500, "Bio must be less than 500 characters").optional(),
    dateOfBirth: z.coerce.date().optional(),
    locale: z.string().min(2).max(10).optional(),
    timezone: z.string().max(50).optional()
  }).optional()
});
var updateConsentsSchema = z.object({
  marketing: z.boolean().optional(),
  profiling: z.boolean().optional()
});
var userIdParamSchema = z.object({
  id: objectIdSchema
});
var userFilterSchema = z.object({
  role: userRoleSchema.optional(),
  status: userStatusSchema.optional(),
  email: z.string().optional(),
  q: z.string().trim().max(100, "Search query must be less than 100 characters").optional(),
  // Filtro segnalazioni antifrode (validateQuery strippa i param non nello schema).
  fraud: z.enum(["flagged", "confirmed", "cleared", "any"]).optional()
});
var grantGiftCreditSchema = z.object({
  amount: z.number().positive("L'importo deve essere maggiore di zero"),
  reason: z.string().trim().min(1, "Il motivo \xE8 obbligatorio")
});
var leadQuerySchema = paginationSchema.extend({
  type: z.enum(["USER", "PROFESSIONAL"]).optional(),
  search: z.string().max(100).optional()
});
var createPopupLeadSchema = z.object({
  email: z.string().email("Formato email non valido").toLowerCase().trim(),
  marketingConsent: z.literal(true, { errorMap: () => ({ message: "Consenso obbligatorio" }) })
});
var updateOpenAISettingsSchema = z.object({
  apiKey: z.string().optional(),
  model: z.string().min(1, "Il modello \xE8 obbligatorio"),
  enabled: z.boolean()
});
var updateGeminiSettingsSchema = z.object({
  apiKey: z.string().optional(),
  model: z.string().min(1, "Il modello \xE8 obbligatorio"),
  enabled: z.boolean()
});
var updateTranscriptionSettingsSchema = z.object({
  activeProvider: z.enum(["openai", "gemini"])
});
var updateSESSettingsSchema = z.object({
  accessKeyId: z.string().optional(),
  secretAccessKey: z.string().optional(),
  region: z.string().min(1, "La regione \xE8 obbligatoria"),
  fromEmail: z.union([z.string().email("Formato email non valido"), z.literal("")]),
  enabled: z.boolean()
});
var updateSMTPSettingsSchema = z.object({
  host: z.string().min(1, "L'host \xE8 obbligatorio"),
  port: z.coerce.number().min(1).max(65535),
  username: z.string().min(1, "L'username \xE8 obbligatorio"),
  password: z.string().optional(),
  fromEmail: z.string().email("Formato email non valido"),
  secure: z.boolean(),
  enabled: z.boolean()
});
var updatePlatformSettingsSchema = z.object({
  registrationEnabled: z.boolean(),
  waitlistEnabled: z.boolean().optional()
});
var updateStripeSettingsSchema = z.object({
  publishableKey: z.string().optional(),
  secretKey: z.string().optional(),
  webhookSecret: z.string().optional(),
  enabled: z.boolean().optional()
});
var updateSmsSettingsSchema = z.object({
  applicationKey: z.string().optional(),
  applicationSecret: z.string().optional(),
  consumerKey: z.string().optional(),
  serviceName: z.string().optional(),
  sender: z.string().trim().min(1).max(11, "Il mittente alfanumerico pu\xF2 avere al massimo 11 caratteri").regex(/[A-Za-z]/, "Il mittente deve contenere almeno una lettera").optional(),
  enabled: z.boolean().optional()
});
var updateWebrtcSettingsSchema = z.object({
  turnUrl: z.string().optional(),
  turnUsername: z.string().optional(),
  turnCredential: z.string().optional(),
  enabled: z.boolean().optional()
});
var updateTurnstileSettingsSchema = z.object({
  siteKey: z.string().optional(),
  secretKey: z.string().optional(),
  enabled: z.boolean().optional()
});
var updatePhoneLookupSettingsSchema = z.object({
  enabled: z.boolean()
});
var updateRegistrationCountrySchema = z.object({
  enabled: z.boolean().optional(),
  allowedDials: z.array(z.string().regex(/^\+\d{1,4}$/, "Prefisso non valido")).optional()
});
var updateTwilioVoiceSettingsSchema = z.object({
  accountSid: z.string().optional(),
  authToken: z.string().optional(),
  fromNumber: z.union([
    z.string().regex(/^\+[1-9]\d{6,14}$/, "Numero non valido (formato E.164, es. +39\u2026)"),
    z.literal("")
  ]).optional(),
  // Incidente 2026-07-07: un testo di prova con un nome reale ("Vincenzo") al
  // posto del placeholder è finito in prod e ogni chiamata di escalation lo
  // annunciava. Un messaggio personalizzato DEVE contenere {utente} (sostituito
  // col nome del chiamante); vuoto = fallback al default, che lo contiene già.
  voiceMessage: z.string().max(500).refine((v) => v.trim() === "" || /\{utente\}/i.test(v), {
    message: "Il messaggio deve contenere il placeholder {utente} (viene sostituito col nome del chiamante). Non usare un nome fisso."
  }).optional(),
  audioUrl: z.union([z.string().url("URL audio non valido"), z.literal("")]).optional(),
  voice: z.string().max(40).optional(),
  enabled: z.boolean().optional()
});
var updateWelcomeBannerSchema = z.object({
  enabled: z.boolean().optional(),
  imageUrl: z.union([z.string().url("URL immagine non valido"), z.literal("")]).optional(),
  reshowDays: z.coerce.number().int().min(0).max(365).optional()
});
var updateGoogleAdsSettingsSchema = z.object({
  enabled: z.boolean().optional(),
  tagId: z.union([
    z.string().regex(/^AW-\d+$/, "Formato non valido (es. AW-123456789)"),
    z.literal("")
  ]).optional(),
  // Etichetta dell'azione di conversione "Acquisto": la parte dopo la barra
  // nel send_to di Google (AW-123456789/XOU_CJ-wkN8cEM-d9qFE). '' = nessuna
  // conversione inviata.
  purchaseLabel: z.union([
    z.string().regex(/^[A-Za-z0-9_-]+$/, "Formato non valido (es. XOU_CJ-wkN8cEM-d9qFE)"),
    z.literal("")
  ]).optional()
}).strict();
var updateMetaPixelSettingsSchema = z.object({
  enabled: z.boolean().optional(),
  pixelId: z.union([
    z.string().regex(/^\d{5,20}$/, "Formato non valido (solo cifre, es. 2178436376339517)"),
    z.literal("")
  ]).optional(),
  events: z.object({
    pageView: z.boolean().optional(),
    completeRegistration: z.boolean().optional(),
    purchase: z.boolean().optional()
  }).strict().optional()
}).strict();
var sendTestSmsSchema = z.object({
  to: z.string().regex(/^\+[1-9]\d{6,14}$/, "Numero non valido (formato E.164, es. +39\u2026)"),
  body: z.string().min(1).max(1600).optional()
});
var sendTestVoiceCallSchema = z.object({
  to: z.string().regex(/^\+[1-9]\d{6,14}$/, "Numero non valido (formato E.164, es. +39\u2026)"),
  message: z.string().min(1).max(500).optional()
});
var updatePayoutSettingsSchema = z.object({
  commissionRate: z.number().min(0).max(1),
  fixedFeePerCall: z.number().min(0)
});
var updateServicesSettingsSchema = z.object({
  openai: updateOpenAISettingsSchema.optional(),
  gemini: updateGeminiSettingsSchema.optional(),
  transcription: updateTranscriptionSettingsSchema.optional(),
  email: z.object({
    activeProvider: z.enum(["ses", "smtp", "none"]),
    ses: updateSESSettingsSchema.optional(),
    smtp: updateSMTPSettingsSchema.optional()
  }).optional(),
  platform: updatePlatformSettingsSchema.optional(),
  payout: updatePayoutSettingsSchema.optional()
});
var verificationStatusValues = ["pending", "in_review", "verified", "rejected"];
var verificationStatusSchema = z.enum(verificationStatusValues);
var workingHoursSchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  startTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format (HH:mm)"),
  endTime: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format (HH:mm)")
}).refine(
  (data) => data.startTime < data.endTime,
  { message: "Start time must be before end time" }
);
var serviceChannelsSchema = z.object({
  chat: z.boolean(),
  audioCall: z.boolean(),
  videoCall: z.boolean()
});
var updateServiceChannelsSchema = serviceChannelsSchema.partial().refine((channels) => Object.keys(channels).length > 0, {
  message: "At least one service channel is required"
});
var availabilitySchema = z.object({
  isAvailable: z.boolean(),
  workingHours: z.array(workingHoursSchema).optional(),
  timezone: z.string().min(1).max(50),
  // Free-text note shown on the public profile under the hours.
  notes: z.string().max(300, "Massimo 300 caratteri").optional().nullable()
});
var createProfessionalSchema = z.object({
  userId: objectIdSchema,
  title: z.string().min(2, "Title must be at least 2 characters").max(100),
  specializations: z.array(z.enum(SPECIALIZATIONS)).min(1, "At least one specialization is required"),
  experience: z.number().int().min(0).max(50),
  education: z.string().max(500).optional(),
  certifications: z.array(z.string().max(200)).optional(),
  hourlyRate: z.number().positive().optional(),
  costPerMinute: z.number().min(1.5, "Il costo minimo \xE8 1.50 EUR/min").max(10, "Il costo massimo \xE8 10.00 EUR/min").optional(),
  currency: z.string().length(3).toUpperCase().optional(),
  // Accept a valid URL, or an empty string meaning "clear the video".
  videoUrl: z.string().url("Invalid video URL").or(z.literal("")).optional(),
  profileDescription: z.string().max(5e3).optional()
});
var updateProfessionalSchema = z.object({
  title: z.string().min(2).max(100).optional(),
  specializations: z.array(z.enum(SPECIALIZATIONS)).min(1).optional(),
  experience: z.number().int().min(0).max(50).optional(),
  education: z.string().max(500).optional(),
  certifications: z.array(z.string().max(200)).optional(),
  hourlyRate: z.number().positive().optional(),
  costPerMinute: z.number().min(1.5, "Il costo minimo \xE8 1.50 EUR/min").max(10, "Il costo massimo \xE8 10.00 EUR/min").optional(),
  currency: z.string().length(3).toUpperCase().optional(),
  // Accept a valid URL, or an empty string meaning "clear the video".
  videoUrl: z.string().url("Invalid video URL").or(z.literal("")).optional(),
  profileDescription: z.string().max(5e3).optional(),
  availability: availabilitySchema.optional(),
  serviceChannels: serviceChannelsSchema.optional()
});
function isValidIban(value) {
  const iban = value.replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    const part = code >= 65 ? String(code - 55) : ch;
    for (const d of part) {
      remainder = (remainder * 10 + (d.charCodeAt(0) - 48)) % 97;
    }
  }
  return remainder === 1;
}
var ibanSchema = z.string().min(1, "L'IBAN \xE8 obbligatorio").transform((v) => v.replace(/\s+/g, "").toUpperCase()).refine((v) => /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(v), "Formato IBAN non valido").refine(isValidIban, "IBAN non valido: controlla le cifre");
var setIbanSchema = z.object({
  iban: ibanSchema
});
var professionalIdParamSchema = z.object({
  id: objectIdSchema
});
var verifyProfessionalSchema = z.object({
  verificationStatus: verificationStatusSchema,
  rejectionReason: z.string().max(500).optional(),
  adminNotes: z.string().max(1e3).optional()
}).refine(
  (data) => {
    if (data.verificationStatus === "rejected" && !data.rejectionReason) {
      return false;
    }
    return true;
  },
  { message: "Rejection reason is required when rejecting a professional", path: ["rejectionReason"] }
).refine(
  (data) => {
    if (data.verificationStatus === "verified" && !data.adminNotes) {
      return false;
    }
    return true;
  },
  { message: "Le note admin sono obbligatorie per l'approvazione", path: ["adminNotes"] }
);
var professionalFilterSchema = paginationSchema.extend({
  specialization: z.string().optional(),
  minExperience: z.coerce.number().int().min(0).optional(),
  maxExperience: z.coerce.number().int().max(50).optional(),
  minRate: z.coerce.number().positive().optional(),
  maxRate: z.coerce.number().positive().optional(),
  // 'approved' is a virtual admin-only filter: questionnaire approved but the
  // consultant is not yet published (verificationStatus still pending).
  verificationStatus: z.enum([...verificationStatusValues, "approved"]).optional(),
  onlineStatus: z.enum(["online", "busy", "offline", "paused"]).optional(),
  isAvailable: z.coerce.boolean().optional(),
  search: z.string().max(100).optional()
});
var publicProfessionalFilterSchema = paginationSchema.extend({
  // Page size is visitor-selectable on the directory (20/50/100/200/Tutti).
  // 'all' is a sentinel meaning "no page limit"; numeric values are capped at 200.
  limit: z.union([z.literal("all"), z.coerce.number().int().min(1).max(200)]).default(20),
  specialization: z.string().optional(),
  sortBy: z.enum(["rating", "price", "reviews", "experience"]).optional(),
  minRate: z.coerce.number().min(0).optional(),
  maxRate: z.coerce.number().positive().optional(),
  search: z.string().max(100).optional(),
  available: z.coerce.boolean().optional()
});
var professionalReviewsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(10)
});
var suspendProfessionalSchema = z.object({
  reason: z.string().min(1, "Il motivo \xE8 obbligatorio").max(1e3, "Massimo 1000 caratteri")
});
var forceOfflineSchema = z.object({
  reason: z.string().min(1, "Il motivo \xE8 obbligatorio").max(1e3, "Massimo 1000 caratteri")
});
var requestChangesSchema = z.object({
  notes: z.string().min(1, "Le note sono obbligatorie").max(1e3, "Massimo 1000 caratteri")
});
var updateEmailTemplateSchema = z.object({
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(5e4),
  isActive: z.boolean().optional()
});
var linguaLivelloSchema = z.enum(["base", "intermedio", "fluente", "madrelingua"]);
var linguaSchema = z.object({
  lingua: z.string().min(1, "Lingua richiesta"),
  livello: linguaLivelloSchema
});
var esperienzaSchema = z.enum(["meno_1_anno", "1_3_anni", "3_5_anni", "oltre_5_anni"]);
var strumentoSchema = z.enum([
  "tarocchi",
  "sibille",
  "oracoli",
  "rune",
  "pendolo",
  "canalizzazione",
  "altro"
]);
var ambitoSchema = z.enum([
  "amore",
  "lavoro",
  "finanze",
  "crescita_personale",
  "spirituale",
  "altro"
]);
var situazioneLavorativaSchema = z.enum([
  "autonomo",
  "dipendente",
  "cerca_occupazione",
  "non_occupato",
  "studente",
  "pensionato",
  "altro"
]);
var partitaIvaSchema = z.enum(["si", "no", "disponibile"]);
var modalitaConsulenzaSchema = z.enum(["chat", "chiamata", "video"]);
var disponibilitaSettimanaleSchema = z.enum(["meno_10", "10_20", "20_30", "oltre_30"]);
var titoloDiStudioSchema = z.enum(["nessuno", "licenza_media", "diploma", "laurea", "altro"]);
var stileDiLetturaSchema = z.enum([
  "diretto",
  "empatico",
  "motivazionale",
  "spirituale",
  "predittivo",
  "altro"
]);
var durataMediaConsultoSchema = z.enum(["10_15", "20_30", "oltre_30"]);
var siNoDipendeSchema = z.enum(["si", "no", "dipende"]);
var siNoDaValutareSchema = z.enum(["si", "no", "da_valutare"]);
var step1Schema = z.object({
  nome: z.string().min(1, "Nome richiesto").max(50).trim(),
  cognome: z.string().min(1, "Cognome richiesto").max(50).trim(),
  email: z.string().min(1, "Email richiesta").email("Email non valida").toLowerCase().trim(),
  telefono: z.string().min(6, "Telefono richiesto").max(20).regex(/^[\d\s\-+()]+$/, "Formato telefono non valido"),
  citta: z.string().min(1, "Citt\xE0 richiesta").max(100).trim(),
  regione: z.string().min(1, "Regione richiesta").max(100).trim(),
  lingue: z.array(linguaSchema).min(1, "Almeno una lingua richiesta"),
  esperienza: esperienzaSchema,
  strumenti: z.array(strumentoSchema).min(1, "Almeno uno strumento richiesto"),
  ambiti: z.array(ambitoSchema).min(1, "Almeno un ambito richiesto"),
  situazioneLavorativa: situazioneLavorativaSchema,
  partitaIva: partitaIvaSchema,
  modalitaConsulenza: z.array(modalitaConsulenzaSchema).min(1, "Almeno una modalit\xE0 richiesta"),
  disponibilitaSettimanale: disponibilitaSettimanaleSchema,
  foto: z.string().min(1, "Foto profilo richiesta")
});
var step2Schema = z.object({
  titoloDiStudio: titoloDiStudioSchema,
  ultimaOccupazione: z.string().max(200).optional(),
  praticaDa: z.string().min(1, "Campo richiesto").max(200),
  corsiOAutodidatta: z.string().min(1, "Campo richiesto").max(1e3),
  attestati: z.string().url("URL attestati non valido").optional(),
  piattaformeOnline: z.string().min(1, "Campo richiesto").max(500),
  stileDiLettura: z.array(stileDiLetturaSchema).min(1, "Almeno uno stile richiesto"),
  durataMediaConsulto: durataMediaConsultoSchema,
  connessioneStabile: z.boolean()
});
var step3Schema = z.object({
  gestioneStress: z.string().min(1, "Campo richiesto").max(2e3),
  temiNonTrattati: z.string().min(1, "Campo richiesto").max(2e3),
  riservatezza: z.string().min(1, "Campo richiesto").max(2e3),
  gestioneRichiesteDelicate: z.string().min(1, "Campo richiesto").max(2e3)
});
var step4Schema = z.object({
  giorniFasceOrarie: z.string().min(1, "Campo richiesto").max(2e3),
  disponibileTurni: siNoDaValutareSchema,
  partitaIvaDettaglio: z.string().min(1, "Campo richiesto").max(500),
  accettazioneNDA: z.boolean(),
  accettazioneRegolamento: z.boolean()
});
var step5Schema = z.object({
  oreSettimanaliGarantite: z.string().min(1, "Campo richiesto").max(200),
  disponibileOnlineSenzaConsulti: siNoDipendeSchema,
  gestioneImprevisti: z.string().min(1, "Campo richiesto").max(2e3),
  competenzeDigitali: z.number().int().min(1).max(5),
  rispostaChat2Min: siNoDipendeSchema,
  lamenteleRicevute: z.string().min(1, "Campo richiesto").max(2e3)
});
var step6Schema = z.object({
  scenario1: z.string().min(600, "Minimo 600 caratteri").max(5e3),
  scenario2: z.string().min(600, "Minimo 600 caratteri").max(5e3),
  scenario3: z.string().min(600, "Minimo 600 caratteri").max(5e3)
});
var step7Schema = z.object({
  puntiDiForza: z.string().min(1, "Campo richiesto").max(2e3),
  puntiDeboli: z.string().min(1, "Campo richiesto").max(2e3),
  migliorare: z.string().min(1, "Campo richiesto").max(2e3),
  percheKlaro7: z.string().min(1, "Campo richiesto").max(2e3),
  obiettivi: z.string().min(1, "Campo richiesto").max(2e3),
  cosaTiDistingue: z.string().min(1, "Campo richiesto").max(2e3)
});
var step8Schema = z.object({
  disponibileProvaLettura: z.boolean(),
  situazioneDifficile: z.string().min(1, "Campo richiesto").max(2e3)
});
var step9Schema = z.object({
  accettaPrivacy: z.literal(true, { errorMap: () => ({ message: "Devi accettare la privacy policy" }) }),
  accettaNDA: z.literal(true, { errorMap: () => ({ message: "Devi accettare il NDA" }) }),
  accettaRegolamento: z.literal(true, { errorMap: () => ({ message: "Devi accettare il regolamento" }) })
});
var stepSchemas = {
  1: step1Schema,
  2: step2Schema,
  3: step3Schema,
  4: step4Schema,
  5: step5Schema,
  6: step6Schema,
  7: step7Schema,
  8: step8Schema,
  9: step9Schema
};
var saveStepSchema = z.object({
  step: z.number().int().min(1).max(9),
  answers: z.record(z.unknown())
});
function buildFieldSchema(field) {
  const rules = field.validation ?? {};
  switch (field.type) {
    case "text":
    case "tel": {
      let schema = z.string();
      if (field.required) {
        schema = schema.min(1, "Campo richiesto");
      }
      if (rules.minLength) {
        schema = schema.min(rules.minLength, `Minimo ${rules.minLength} caratteri`);
      }
      if (rules.maxLength) {
        schema = schema.max(rules.maxLength, `Massimo ${rules.maxLength} caratteri`);
      }
      if (rules.pattern) {
        schema = schema.regex(new RegExp(rules.pattern), "Formato non valido");
      }
      if (rules.isUrl) {
        schema = schema.url("URL non valido");
      }
      if (field.required) {
        return schema;
      }
      return schema.optional();
    }
    case "email": {
      let schema = z.string();
      if (field.required) {
        schema = schema.min(1, "Email richiesta");
      }
      schema = schema.email("Email non valida").toLowerCase().trim();
      return field.required ? schema : schema.optional();
    }
    case "textarea": {
      let schema = z.string();
      if (field.required) {
        schema = schema.min(1, "Campo richiesto");
      }
      if (rules.minLength) {
        schema = schema.min(rules.minLength, `Minimo ${rules.minLength} caratteri`);
      }
      if (rules.maxLength) {
        schema = schema.max(rules.maxLength, `Massimo ${rules.maxLength} caratteri`);
      }
      return field.required ? schema : schema.optional();
    }
    case "select":
    case "radio": {
      if (field.options && field.options.length > 0) {
        const values = field.options.map((o) => o.value);
        return z.enum(values);
      }
      return field.required ? z.string().min(1, "Campo richiesto") : z.string().optional();
    }
    case "checkbox-group": {
      if (field.options && field.options.length > 0) {
        const values = field.options.map((o) => o.value);
        const itemSchema = z.enum(values);
        return field.required ? z.array(itemSchema).min(1, "Almeno un'opzione richiesta") : z.array(itemSchema);
      }
      return field.required ? z.array(z.string()).min(1, "Almeno un'opzione richiesta") : z.array(z.string());
    }
    case "boolean": {
      if (rules.isLiteral) {
        return z.literal(true, {
          errorMap: () => ({ message: rules.literalMessage || "Devi accettare" })
        });
      }
      return z.boolean();
    }
    case "range": {
      let schema = z.number().int();
      if (rules.min != null) {
        schema = schema.min(rules.min);
      }
      if (rules.max != null) {
        schema = schema.max(rules.max);
      }
      return schema;
    }
    case "lingue": {
      const linguaSchema2 = z.object({
        lingua: z.string().min(1, "Lingua richiesta"),
        livello: z.enum(["base", "intermedio", "fluente", "madrelingua"])
      });
      return field.required ? z.array(linguaSchema2).min(1, "Almeno una lingua richiesta") : z.array(linguaSchema2);
    }
    default:
      return z.unknown();
  }
}
function buildStepSchema(stepDef) {
  const shape = {};
  for (const field of stepDef.fields) {
    shape[field.key] = buildFieldSchema(field);
  }
  return z.object(shape);
}
function buildAllStepSchemas(steps) {
  const schemas = {};
  for (const step of steps) {
    schemas[step.step] = buildStepSchema(step);
  }
  return schemas;
}
var fieldOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1)
});
var fieldValidationRulesSchema = z.object({
  minLength: z.number().int().min(0).optional(),
  maxLength: z.number().int().min(1).optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  isUrl: z.boolean().optional(),
  isEmail: z.boolean().optional(),
  isLiteral: z.boolean().optional(),
  literalMessage: z.string().optional()
}).optional();
var fieldDefinitionSchema = z.object({
  key: z.string().min(1).max(100).regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, "Key deve essere un identificatore valido"),
  label: z.string().min(1).max(500),
  type: z.enum(["text", "email", "tel", "select", "radio", "checkbox-group", "textarea", "boolean", "range", "lingue"]),
  required: z.boolean(),
  options: z.array(fieldOptionSchema).optional(),
  validation: fieldValidationRulesSchema,
  placeholder: z.string().max(500).optional(),
  helperText: z.string().max(500).optional()
});
var updateStepDefinitionSchema = z.object({
  step: z.number().int().min(1).max(9),
  name: z.string().min(1).max(200),
  description: z.string().min(1).max(1e3),
  fields: z.array(fieldDefinitionSchema).min(1, "Almeno un campo richiesto")
});
var walletTransactionTypeValues = [
  "recharge",
  "consultation_charge",
  "free_minutes_credit",
  "refund",
  "call_charge",
  "chat_charge",
  "credit_reservation",
  "reservation_release",
  "recharge_bonus"
];
var walletTransactionTypeSchema = z.enum(walletTransactionTypeValues);
var rechargeWalletSchema = z.object({
  amount: z.number().positive("Amount must be positive"),
  description: z.string().max(500).optional()
});
var rechargeCheckoutSchema = z.object({
  amount: z.number()
});
var walletIdParamSchema = z.object({
  id: objectIdSchema
});
var walletTransactionFilterSchema = paginationSchema.extend({
  type: walletTransactionTypeSchema.optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var callSessionStatusValues = [
  "ringing",
  "active",
  "completed",
  "missed",
  "rejected",
  "failed",
  "cancelled"
];
var callTypeValues = ["audio", "video"];
var callSessionStatusSchema = z.enum(callSessionStatusValues);
var callTypeSchema = z.enum(callTypeValues);
var callSessionIdParamSchema = z.object({
  id: objectIdSchema
});
var starRating = z.number().int().min(1).max(5);
var rateCallSchema = z.object({
  ratings: z.object({
    overall: starRating,
    professionalism: starRating,
    clarity: starRating,
    punctuality: starRating,
    availability: starRating
  }),
  feedback: z.string().max(500).optional()
});
var callSessionFilterSchema = paginationSchema.extend({
  status: callSessionStatusSchema.optional(),
  professionalId: objectIdSchema.optional(),
  // Admin-only filter: list the calls a specific user made (ignored for other roles)
  callerId: objectIdSchema.optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  search: z.string().max(100).optional()
});
var consultationStatusValues = ["pending", "active", "completed", "cancelled"];
var consultationStatusSchema = z.enum(consultationStatusValues);
var createConsultationSchema = z.object({
  professionalId: objectIdSchema,
  startedAt: z.coerce.date(),
  durationMinutes: z.number().int().min(1, "Duration must be at least 1 minute"),
  costPerMinute: z.number().min(0, "Cost per minute must be non-negative"),
  notes: z.string().max(2e3).optional()
});
var updateConsultationSchema = z.object({
  status: consultationStatusSchema.optional(),
  endedAt: z.coerce.date().optional(),
  durationMinutes: z.number().int().min(0).optional(),
  totalCost: z.number().min(0).optional(),
  notes: z.string().max(2e3).optional(),
  rating: z.number().int().min(1).max(5).optional()
});
var consultationIdParamSchema = z.object({
  id: objectIdSchema
});
var consultationFilterSchema = paginationSchema.extend({
  status: consultationStatusSchema.optional(),
  professionalId: objectIdSchema.optional(),
  userId: objectIdSchema.optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional()
});
var favoriteProfessionalIdParamSchema = z.object({
  professionalId: objectIdSchema
});
var chatMessageStatusValues = ["sent", "delivered", "read"];
var chatMessageStatusSchema = z.enum(chatMessageStatusValues);
var sendMessageSchema = z.object({
  conversationId: objectIdSchema.optional(),
  professionalId: objectIdSchema.optional(),
  userId: objectIdSchema.optional(),
  content: z.string().min(1, "Message content is required").max(5e3, "Message content must be less than 5000 characters")
}).refine(
  (data) => data.conversationId || data.professionalId || data.userId,
  { message: "Either conversationId, professionalId or userId is required" }
);
var conversationIdParamSchema = z.object({
  conversationId: objectIdSchema
});
var chatMessageFilterSchema = z.object({
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50).optional()
});
var messageIdParamSchema = z.object({
  id: objectIdSchema
});
var adminStarredMessagesFilterSchema = z.object({
  userId: objectIdSchema.optional(),
  professionalId: objectIdSchema.optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20).optional()
});
var adminConversationsFilterSchema = z.object({
  q: z.string().trim().min(1).max(100).optional(),
  professionalId: objectIdSchema.optional(),
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20).optional()
});
var navLinkSchema = z.object({
  label: z.string().min(1).max(100),
  href: z.string().min(1).max(500)
});
var navbarContentSchema = z.object({
  logo: z.object({
    url: z.string().min(1).max(500),
    alt: z.string().min(1).max(200)
  }),
  navLinks: z.array(navLinkSchema).max(10)
});
var ctaCardSchema = z.object({
  title: z.string().min(1).max(200),
  subtitle: z.string().min(1).max(500),
  buttonLabel: z.string().min(1).max(100),
  type: z.enum(["user", "professional"])
});
var tarotImageSchema = z.object({
  src: z.string().min(1).max(500),
  alt: z.string().min(1).max(200),
  position: z.enum(["left", "right"]),
  rotation: z.number().min(-180).max(180)
});
var heroContentSchema = z.object({
  badge: z.string().min(1).max(200),
  title: z.string().min(1).max(200),
  subtitle: z.string().min(1).max(500),
  tagline: z.string().min(1).max(1e3),
  ctaCards: z.array(ctaCardSchema).max(4),
  tarotImages: z.array(tarotImageSchema).max(10)
});
var bonusSectionContentSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().min(1).max(2e3)
});
var featureItemSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(1e3),
  iconSvg: z.string().min(1).max(1e4)
});
var featuresContentSchema = z.object({
  sectionTitle: z.string().min(1).max(200),
  features: z.array(featureItemSchema).max(6)
});
var trustBadgeSchema = z.object({
  label: z.string().min(1).max(200),
  iconSvg: z.string().min(1).max(1e4)
});
var professionalsContentSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().min(1).max(2e3),
  ctaLabel: z.string().min(1).max(200),
  trustBadges: z.array(trustBadgeSchema).max(10)
});
var legalLinkSchema = z.object({
  label: z.string().min(1).max(100),
  href: z.string().min(1).max(500)
});
var footerContentSchema = z.object({
  companyInfo: z.string().min(1).max(500),
  legalLinks: z.array(legalLinkSchema).max(10),
  copyright: z.string().min(1).max(500)
});
var sectionContentSchemas = {
  navbar: navbarContentSchema,
  hero: heroContentSchema,
  bonus: bonusSectionContentSchema,
  features: featuresContentSchema,
  professionals: professionalsContentSchema,
  footer: footerContentSchema
};
var sectionSpacingSchema = z.object({
  marginTop: z.number().min(0).max(200),
  marginBottom: z.number().min(0).max(200),
  paddingTop: z.number().min(0).max(200),
  paddingBottom: z.number().min(0).max(200)
});
var elementLayoutOverrideSchema = z.object({
  translateX: z.number().min(-300).max(300),
  translateY: z.number().min(-300).max(300),
  marginTop: z.number().min(0).max(200).optional(),
  marginBottom: z.number().min(0).max(200).optional(),
  paddingTop: z.number().min(0).max(200).optional(),
  paddingBottom: z.number().min(0).max(200).optional()
});
var layoutOverridesSchema = z.record(
  z.string().max(50),
  elementLayoutOverrideSchema
);
var updateSectionSchema = z.object({
  content: z.record(z.unknown()).optional(),
  visible: z.boolean().optional(),
  spacing: sectionSpacingSchema.optional(),
  layoutOverrides: layoutOverridesSchema.optional()
});
var updateSectionsOrderSchema = z.object({
  order: z.array(z.enum(["navbar", "hero", "bonus", "features", "professionals", "footer"]))
});
var updateSeoSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().min(1).max(500),
  keywords: z.array(z.string().min(1).max(100)).max(20),
  ogImage: z.string().max(500).optional()
});
var updateThemeSchema = z.object({
  primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Invalid hex color"),
  secondaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Invalid hex color"),
  animationsEnabled: z.boolean()
});
var publishSchema = z.object({
  note: z.string().max(500).optional()
});
var sectionTypeParamSchema = z.object({
  sectionType: z.enum(["navbar", "hero", "bonus", "features", "professionals", "footer"])
});
var versionIdParamSchema = z.object({
  versionId: z.string().regex(/^[a-fA-F0-9]{24}$/, "Invalid ID format")
});
var createAvailabilityWatchSchema = z.object({
  professionalId: objectIdSchema
});
var availabilityWatchProfessionalIdParamSchema = z.object({
  professionalId: objectIdSchema
});
var availabilityWatchListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  page: z.coerce.number().int().min(1).default(1)
});
var cassaProfessionaleSchema = z.object({
  nome: z.string().min(1, "Nome cassa \xE8 obbligatorio"),
  percentuale: z.number().min(0, "Percentuale cassa non pu\xF2 essere negativa")
});
var CF_ITALIANO = /^[A-Z0-9]{16}$/;
var ID_FISCALE_ESTERO = /^[A-Z0-9-]{6,32}$/;
var PIVA_ITALIANA = /^[0-9]{11}$/;
var VAT_ESTERA = /^[A-Z0-9]{4,20}$/;
var updateFiscalProfileSchema = z.object({
  codiceFiscale: z.string().min(1, "Codice fiscale richiesto").transform((v) => v.toUpperCase()),
  dataNascita: z.string().min(1, "Data di nascita richiesta").refine((v) => !Number.isNaN(new Date(v).getTime()), "Data di nascita non valida"),
  comuneNascita: z.string().min(1, "Comune di nascita is required"),
  indirizzoResidenza: z.string().min(1, "Indirizzo di residenza is required"),
  regimeFiscale: z.enum(["occasionale", "forfettario", "ordinario", "con_cassa", "estero"]).optional().default("occasionale"),
  partitaIVA: z.string().transform((v) => v.toUpperCase()).optional(),
  paeseEstero: z.string().max(100).optional().nullable(),
  ivaPercentuale: z.number().min(0).max(100).optional(),
  cassaProfessionale: cassaProfessionaleSchema.optional().nullable(),
  // Business data — only meaningful for partita IVA regimes, always optional.
  ragioneSociale: z.string().max(200).optional().nullable(),
  sedeFiscale: z.string().max(300).optional().nullable(),
  numeroREA: z.string().max(50).optional().nullable()
}).superRefine((data, ctx) => {
  const regime = data.regimeFiscale ?? "occasionale";
  const isEstero = regime === "estero";
  if (isEstero) {
    if (!ID_FISCALE_ESTERO.test(data.codiceFiscale)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Identificativo fiscale estero non valido (6-32 caratteri alfanumerici)",
        path: ["codiceFiscale"]
      });
    }
  } else if (!CF_ITALIANO.test(data.codiceFiscale)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Codice fiscale must be exactly 16 alphanumeric characters",
      path: ["codiceFiscale"]
    });
  }
  if (data.partitaIVA !== void 0) {
    const pattern = isEstero ? VAT_ESTERA : PIVA_ITALIANA;
    const message = isEstero ? "Partita IVA estera non valida (4-20 caratteri alfanumerici, es. ESB1234567X)" : "Partita IVA deve essere 11 cifre";
    if (!pattern.test(data.partitaIVA)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ["partitaIVA"] });
    }
  }
  if (regime === "forfettario" || regime === "ordinario" || isEstero) {
    if (!data.partitaIVA) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Partita IVA \xE8 obbligatoria per il regime " + regime,
        path: ["partitaIVA"]
      });
    }
  }
  if (isEstero && (!data.paeseEstero || data.paeseEstero.trim() === "")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Paese di residenza fiscale \xE8 obbligatorio per il regime estero",
      path: ["paeseEstero"]
    });
  }
  if (regime === "con_cassa") {
    if (!data.partitaIVA) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Partita IVA \xE8 obbligatoria per il regime con_cassa",
        path: ["partitaIVA"]
      });
    }
    if (!data.cassaProfessionale || !data.cassaProfessionale.nome) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Cassa professionale \xE8 obbligatoria per il regime con_cassa",
        path: ["cassaProfessionale"]
      });
    }
  }
});
var requestPhoneCodeSchema = z.object({
  phone: z.string().regex(/^\+[1-9]\d{6,14}$/, "Numero non valido (formato E.164, es. +39\u2026)")
});
var confirmPhoneCodeSchema = z.object({
  code: z.string().regex(/^\d{4}$/, "Il codice deve avere 4 cifre")
});
var sendSupportMessageSchema = z.object({
  conversationId: objectIdSchema.optional(),
  content: z.string().max(5e3, "Massimo 5000 caratteri").optional().default("")
});
var supportBroadcastSchema = z.object({
  content: z.string().trim().min(1, "Il messaggio non pu\xF2 essere vuoto").max(5e3, "Massimo 5000 caratteri"),
  allProfessionals: z.boolean().optional().default(false),
  professionalIds: z.array(objectIdSchema).optional().default([])
}).refine((d) => d.allProfessionals || d.professionalIds.length > 0, {
  message: "Seleziona almeno un destinatario",
  path: ["professionalIds"]
});
var supportStatusSchema = z.object({
  status: z.enum(["open", "pending", "resolved"]),
  assignedAdminId: objectIdSchema.optional().nullable()
});
var supportAssignSchema = z.object({
  assignedAdminId: objectIdSchema.nullable()
  // null = unassign
});
var supportConversationIdParamSchema = z.object({
  conversationId: objectIdSchema
});
var supportProIdParamSchema = z.object({
  proId: objectIdSchema
});
var supportMessageFilterSchema = z.object({
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50).optional()
});
var sendUserSupportMessageSchema = z.object({
  // Il client manda il form multipart: un campo assente arriva come stringa
  // vuota, che qui vale "non specificato" (il service apre/riusa il thread).
  conversationId: z.preprocess(
    (v) => v === "" ? void 0 : v,
    objectIdSchema.optional()
  ),
  content: z.string().max(5e3, "Massimo 5000 caratteri").optional().default("")
});
var userSupportStatusSchema = z.object({
  status: z.enum(["open", "pending", "resolved"]),
  assignedAdminId: objectIdSchema.optional().nullable()
});
var userSupportAssignSchema = z.object({
  assignedAdminId: objectIdSchema.nullable()
  // null = unassign
});
var userSupportConversationIdParamSchema = z.object({
  conversationId: objectIdSchema
});
var userSupportUserIdParamSchema = z.object({
  userId: objectIdSchema
});
var userSupportAttachmentParamSchema = z.object({
  messageId: objectIdSchema,
  // Indice dell'allegato dentro il messaggio: intero non negativo.
  index: z.coerce.number().int().min(0)
});
var userSupportInboxFilterSchema = z.object({
  status: z.enum(["open", "pending", "resolved"]).optional()
});
var userSupportMessageFilterSchema = z.object({
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50).optional()
});
var seoPageMetaSchema = z.object({
  title: z.string().max(200).optional(),
  description: z.string().max(500).optional(),
  keywords: z.array(z.string().min(1).max(100)).max(20).optional(),
  ogImage: z.string().max(500).optional(),
  noindex: z.boolean().optional()
});
var seoGlobalSchema = z.object({
  siteName: z.string().min(1).max(100),
  titleSuffix: z.string().max(60),
  defaultTitle: z.string().min(1).max(200),
  defaultDescription: z.string().min(1).max(500),
  defaultKeywords: z.array(z.string().min(1).max(100)).max(20),
  defaultOgImage: z.string().max(500).optional()
});
var seoConsultantTemplateSchema = z.object({
  titleTemplate: z.string().min(1).max(200),
  descriptionTemplate: z.string().min(1).max(500)
});
var seoJsonLdSchema = z.object({
  organizationEnabled: z.boolean(),
  organizationName: z.string().min(1).max(100),
  organizationLogo: z.string().max(500).optional(),
  organizationSameAs: z.array(z.string().url().max(300)).max(10),
  websiteEnabled: z.boolean(),
  consultantPersonEnabled: z.boolean(),
  faqPageEnabled: z.boolean()
});
var updateSeoConfigSchema = z.object({
  global: seoGlobalSchema.optional(),
  pages: z.record(z.string().regex(/^\/[a-z0-9/-]*$/), seoPageMetaSchema).optional(),
  consultant: seoConsultantTemplateSchema.optional(),
  jsonLd: seoJsonLdSchema.optional()
});
var replyToReviewSchema = z.object({
  text: z.string().trim().min(1, "La risposta non pu\xF2 essere vuota").max(1e3, "Massimo 1000 caratteri")
});
var reviewReplyParamSchema = z.object({
  callSessionId: objectIdSchema
});
var myReviewsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(50).default(10)
});

export { COUNTRIES, adminConversationsFilterSchema, adminStarredMessagesFilterSchema, availabilitySchema, availabilityWatchListQuerySchema, availabilityWatchProfessionalIdParamSchema, bonusSectionContentSchema, buildAllStepSchemas, buildFieldSchema, buildStepSchema, callSessionFilterSchema, callSessionIdParamSchema, callSessionStatusSchema, callSessionStatusValues, callTypeSchema, callTypeValues, changePasswordSchema, chatMessageFilterSchema, chatMessageStatusSchema, chatMessageStatusValues, completeOnboardingSchema, composeE164, confirmEmailSchema, confirmPhoneCodeSchema, consultationFilterSchema, consultationIdParamSchema, consultationStatusSchema, consultationStatusValues, conversationIdParamSchema, createAvailabilityWatchSchema, createConsultationSchema, createPopupLeadSchema, createProfessionalSchema, createUserSchema, dateRangeSchema, dialPrefixOf, emailSchema, favoriteProfessionalIdParamSchema, featuresContentSchema, footerContentSchema, forceOfflineSchema, grantGiftCreditSchema, heroContentSchema, ibanSchema, isAdult, isDisposableEmailDomain, isItalianNonMobile, isValidIban, leadQuerySchema, loginSchema, messageIdParamSchema, myReviewsQuerySchema, nameSchema, navbarContentSchema, normalizeE164, objectIdSchema, paginationSchema, passwordResetConfirmSchema, passwordResetRequestSchema, passwordSchema, phoneSchema, professionalFilterSchema, professionalIdParamSchema, professionalReviewsQuerySchema, professionalsContentSchema, publicProfessionalFilterSchema, publishSchema, rateCallSchema, rechargeCheckoutSchema, rechargeWalletSchema, refreshTokenSchema, registerSchema, replyToReviewSchema, requestChangesSchema, requestPhoneCodeSchema, reviewReplyParamSchema, saveStepSchema, searchSchema, sectionContentSchemas, sectionSpacingSchema, sectionTypeParamSchema, selfChangePasswordSchema, sendMessageSchema, sendSupportMessageSchema, sendTestSmsSchema, sendTestVoiceCallSchema, sendUserSupportMessageSchema, seoConsultantTemplateSchema, seoGlobalSchema, seoJsonLdSchema, seoPageMetaSchema, setIbanSchema, simplePasswordSchema, step1Schema, step2Schema, step3Schema, step4Schema, step5Schema, step6Schema, step7Schema, step8Schema, step9Schema, stepSchemas, stripPhoneSeparators, supportAssignSchema, supportBroadcastSchema, supportConversationIdParamSchema, supportMessageFilterSchema, supportProIdParamSchema, supportStatusSchema, suspendProfessionalSchema, updateConsentsSchema, updateConsultationSchema, updateEmailTemplateSchema, updateFiscalProfileSchema, updateGoogleAdsSettingsSchema, updateMetaPixelSettingsSchema, updateMyProfileSchema, updateOpenAISettingsSchema, updatePayoutSettingsSchema, updatePhoneLookupSettingsSchema, updateProfessionalSchema, updateRegistrationCountrySchema, updateSESSettingsSchema, updateSMTPSettingsSchema, updateSectionSchema, updateSectionsOrderSchema, updateSeoConfigSchema, updateSeoSchema, updateServiceChannelsSchema, updateServicesSettingsSchema, updateSmsSettingsSchema, updateStepDefinitionSchema, updateStripeSettingsSchema, updateThemeSchema, updateTurnstileSettingsSchema, updateTwilioVoiceSettingsSchema, updateUserSchema, updateWebrtcSettingsSchema, updateWelcomeBannerSchema, urlSchema, userFilterSchema, userIdParamSchema, userRoleSchema, userRoleValues, userStatusSchema, userStatusValues, userSupportAssignSchema, userSupportAttachmentParamSchema, userSupportConversationIdParamSchema, userSupportInboxFilterSchema, userSupportMessageFilterSchema, userSupportStatusSchema, userSupportUserIdParamSchema, uuidSchema, verificationStatusSchema, verificationStatusValues, verifyProfessionalSchema, versionIdParamSchema, walletIdParamSchema, walletTransactionFilterSchema, walletTransactionTypeSchema, walletTransactionTypeValues, workingHoursSchema };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map